import api from '../httpClient';

export interface DashboardCacheMeta {
  source: 'batch' | 'manual';
  section: 'today' | 'overviewAnalytics';
  schemaVersion: number;
  generatedAt: string;
  updatedAt: string;
}

export interface DashboardPendingResponse {
  status: 'pending';
  date: string;
  timezone: string;
  generatedAt: null;
  message?: string;
}

export type DashboardInvoiceDateBasis = 'invoiceDate' | 'paidDate';

export interface DashboardQueryParams {
  date?: string;
  startDate?: string;
  endDate?: string;
  timezone?: string;
  invoiceDateBasis?: DashboardInvoiceDateBasis;
}

export interface DashboardAppointmentItem {
  id: string | number;
  clientId?: string | null;
  clientName?: string | null;
  title?: string | null;
  startTime?: string | null;
  endTime?: string | null;
  timezone?: string | null;
  status?: string | null;
  appointmentType?: string | null;
  meetingLink?: string | null;
  location?: string | null;
}

export interface DashboardMealPlanActionItem {
  id: string;
  clientId?: string | null;
  clientName: string;
  planName?: string | null;
  type: 'expiring' | 'expired';
  status?: string | null;
  generationType?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  expiresAt?: string | null;
  hoursUntilExpiry?: number | null;
  daysExpired?: number | null;
  recommendedAction?: string | null;
}

export interface DashboardInvoiceActionItem {
  id: string;
  invoiceNo?: string | null;
  clientId?: string | null;
  clientName: string;
  status: 'overdue' | 'due' | string;
  invoiceStatus?: string | null;
  dueDate?: string | null;
  totalDue?: number | null;
  currency?: string | null;
  recommendedAction?: string | null;
}

export interface DashboardTodayResponse {
  date: string;
  timezone: string;
  generatedAt: string;
  cache?: DashboardCacheMeta;
  cards: {
    appointmentsToday: {
      count: number;
      nextAppointment: DashboardAppointmentItem | null;
    };
    mealPlansToAction: {
      count: number;
      expiringCount: number;
      expiredCount: number;
    };
    invoicesAndBilling: {
      count: number;
      overdueCount: number;
      dueCount: number;
    };
  };
  appointments: {
    count: number;
    items: DashboardAppointmentItem[];
  };
  mealPlansToAction: {
    counts: {
      expiring: number;
      expired: number;
    };
    expiring: DashboardMealPlanActionItem[];
    expired: DashboardMealPlanActionItem[];
    items: DashboardMealPlanActionItem[];
  };
  invoicesAndBilling: {
    counts: {
      overdue: number;
      due: number;
    };
    items: DashboardInvoiceActionItem[];
  };
}

export interface DashboardRecentPaymentItem {
  id: string;
  invoiceNo?: string | null;
  clientId?: string | null;
  clientName: string;
  title: string;
  amount: number;
  currency?: string | null;
  status: string;
  paidAt?: string | null;
  dueDate?: string | null;
  sortDate?: string | null;
}

export interface DashboardRevenueChartPoint {
  monthKey: string;
  label: string;
  amount: number;
}

export interface DashboardTrendPoint {
  date: string;
  label: string;
  value: number;
}

export interface DashboardConditionItem {
  name: string;
  count: number;
  percent: number;
}

export interface DashboardQuestionnaireCompletionItem {
  questionnaireId: string;
  name: string;
  total: number;
  completed: number;
  percent: number;
}

export interface DashboardOverviewAnalyticsResponse {
  date: string;
  timezone: string;
  generatedAt: string;
  cache?: DashboardCacheMeta;
  period: {
    monthKey: string;
    previousMonthKey: string;
    monthStart: string;
    monthEnd: string;
    startDate?: string;
    endDate?: string;
    previousStart?: string;
    previousEnd?: string;
    granularity?: 'day' | 'week' | 'month';
    isCustomRange?: boolean;
    invoiceDateBasis?: DashboardInvoiceDateBasis;
  };
  cards: {
    totalClients: {
      count: number;
      newThisMonth: number;
      previousNewClients?: number;
      deltaNewClients?: number;
      trendPoints?: DashboardTrendPoint[];
    };
    totalAppointments: {
      count: number;
      deltaThisMonth: number;
      windowDays?: number;
      windowStart?: string;
      windowEnd?: string;
    };
    monthlyRevenue: {
      amount: number;
      previousMonthAmount: number;
      deltaPercent: number;
      currency?: string | null;
      trendPoints?: DashboardTrendPoint[];
    };
    invoicesDue: {
      count: number;
      urgentCount: number;
      pendingAmount: number;
    };
  };
  recentPayments: {
    count: number;
    items: DashboardRecentPaymentItem[];
    totalCollected: number;
  };
  revenue: {
    amount: number;
    previousMonthAmount: number;
    deltaPercent: number;
    chart: DashboardRevenueChartPoint[];
    collected: number;
    pending: number;
    paymentIntervalDaysAvg: number | null;
    paidInvoiceCount: number;
  };
  clientActivity: {
    active: number;
    newThisMonth: number;
    flags?: number;
    conditions: DashboardConditionItem[];
  };
  questionnaireCompletion: {
    items: DashboardQuestionnaireCompletionItem[];
  };
}

export type DashboardTodayApiResponse = DashboardTodayResponse | DashboardPendingResponse;
export type DashboardOverviewAnalyticsApiResponse = DashboardOverviewAnalyticsResponse | DashboardPendingResponse;

const buildDashboardQuery = (params?: DashboardQueryParams): string => {
  const query = new URLSearchParams();
  if (params?.date) query.set('date', params.date);
  if (params?.startDate) query.set('startDate', params.startDate);
  if (params?.endDate) query.set('endDate', params.endDate);
  if (params?.timezone) query.set('timezone', params.timezone);
  if (params?.invoiceDateBasis) query.set('invoiceDateBasis', params.invoiceDateBasis);
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

const unwrapDashboardResponse = <T>(payload: unknown): T => {
  const wrapped = payload as { data?: unknown };
  return (wrapped?.data ?? payload) as T;
};

export const isDashboardPendingResponse = (
  response?: DashboardTodayApiResponse | DashboardOverviewAnalyticsApiResponse
): response is DashboardPendingResponse =>
  Boolean(response && 'status' in response && response.status === 'pending');

export const getDashboardToday = async (
  params?: DashboardQueryParams
): Promise<DashboardTodayApiResponse> => {
  const response = await api.get(`/dashboard/today${buildDashboardQuery(params)}`);
  return unwrapDashboardResponse<DashboardTodayApiResponse>(response.data);
};

export const getDashboardOverviewAnalytics = async (
  params?: DashboardQueryParams
): Promise<DashboardOverviewAnalyticsApiResponse> => {
  const response = await api.get(`/dashboard/overview-analytics${buildDashboardQuery(params)}`);
  return unwrapDashboardResponse<DashboardOverviewAnalyticsApiResponse>(response.data);
};
